import React, { useState, useEffect } from "react";
import axios from "axios";
import { CheckCircle, ChevronDown, ChevronRight, ClipboardList, Clock, Download, Fuel, X } from "lucide-react";
import { Badge, Button, Card, Input, Select, Spinner } from "../shared/ui";
import { useAuth } from "../App";
import { TimeEntryEditor, EditEntryButton } from "../components/timesheets/EntryEditors";
import { AddMissingEntryButton } from "../components/timesheets/AddMissingEntryButton";
import { WorkingAwayCard } from "../components/timesheets/WorkingAwayCard";
import { AskQuestionButton, QueryThread } from "../components/timesheets/QueryThread";
import { uploadReceipt } from "../lib/uploadReceipt";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Timesheets Page - Submit hours, approval workflow, comparison view
const TimesheetsPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("submit"); // submit, pending, approved, compare
  const [loading, setLoading] = useState(true);
  const [timesheets, setTimesheets] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  
  // Submission form state
  const [weekStart, setWeekStart] = useState(() => {
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - today.getDay() + 1);
    return monday.toISOString().split('T')[0];
  });
  const [entries, setEntries] = useState([]);
  const [originalEntries, setOriginalEntries] = useState([]); // Store auto-populated original
  const [reimbursements, setReimbursements] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [autoPopulated, setAutoPopulated] = useState(false);
  
  // Comparison state
  const [selectedUser, setSelectedUser] = useState("");
  const [compareWeek, setCompareWeek] = useState("");
  const [comparison, setComparison] = useState(null);
  const [loadingCompare, setLoadingCompare] = useState(false);

  // Expanded employees in pending-approval view (employee_id -> boolean)
  const [expandedEmployees, setExpandedEmployees] = useState({});

  // Quick-view drawer for timesheet approvals
  const [quickView, setQuickView] = useState(null); // { ts, dayIso, dayLabel, entries, dayReimb, travel, hrs, hasLAA, empName }
  const [editingEntryId, setEditingEntryId] = useState(null);
  // Q&A threads for the currently-open timesheet (keyed by query.id)
  const [queries, setQueries] = useState([]);
  // Pending questions FROM approvers TO the current worker — shown as a banner.
  const [myPendingQueries, setMyPendingQueries] = useState([]);

  const fetchQueriesForTimesheet = async (timesheetId) => {
    if (!timesheetId) { setQueries([]); return; }
    try {
      const r = await axios.get(`${API}/timesheet-queries`, { params: { timesheet_id: timesheetId } });
      setQueries(r.data || []);
    } catch (e) {
      setQueries([]);
    }
  };

  // Whenever the QuickView opens for a new timesheet, refresh its query thread list
  useEffect(() => {
    if (quickView?.ts?.id) fetchQueriesForTimesheet(quickView.ts.id);
    else setQueries([]);
  }, [quickView?.ts?.id]);

  // Pending questions from approvers waiting on the worker's reply
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/timesheet-queries`, { params: { mine: true, status: "pending" } });
        if (!cancelled) setMyPendingQueries(r.data || []);
      } catch (e) {
        if (!cancelled) setMyPendingQueries([]);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, timesheets.length]);

  const isApprover = ['admin', 'manager', 'supervisor'].includes(user?.role);
  const isReimbursementApprover = ['admin', 'manager'].includes(user?.role);
  const canEditEntries = ['admin', 'manager', 'office_staff', 'supervisor'].includes(user?.role);
  const canViewAll = ['admin', 'manager', 'supervisor', 'office_staff'].includes(user?.role);
  const canCompareHours = ['admin', 'manager', 'supervisor'].includes(user?.role); // Supervisor can also compare hours

  const EXPENSE_TYPES = [
    { value: "fuel", label: "Fuel" },
    { value: "tools", label: "Tools" },
    { value: "equipment", label: "Equipment" },
    { value: "meals", label: "Meals" },
    { value: "travel", label: "Travel" },
    { value: "other", label: "Other" }
  ];

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [jobsRes, timesheetsRes] = await Promise.all([
        axios.get(`${API}/jobs`),
        axios.get(`${API}/timesheets${activeTab === 'pending' ? '?status=pending' : activeTab === 'approved' ? '?status=approved' : ''}`)
      ]);
      setJobs(jobsRes.data);
      setTimesheets(timesheetsRes.data);
      
      if (canViewAll) {
        const [usersRes, pendingRes] = await Promise.all([
          axios.get(`${API}/users`),
          axios.get(`${API}/timesheets/pending-approval/count`)
        ]);
        setUsers(usersRes.data);
        setPendingCount(pendingRes.data.pending_count);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleAutoPopulate = async () => {
    try {
      const res = await axios.post(`${API}/timesheets/auto-populate?week_start=${weekStart}`);
      if (res.data.entries && res.data.entries.length > 0) {
        const populatedEntries = res.data.entries.map(e => ({
          date: e.date,
          job_id: e.job_id,
          start_time: e.start_time,
          finish_time: e.finish_time,
          hours: e.hours,
          travel_hours: 0,
          notes: e.notes || "",
          living_away_allowance: false,
          auto_populated: true,
          original_entry_id: e.original_entry_id
        }));
        setEntries(populatedEntries);
        setOriginalEntries(populatedEntries); // Store original
        setAutoPopulated(true);
        alert(`Loaded ${res.data.entries.length} entries from your clock-ins. You can now edit and add travel/LAA.`);
      } else {
        alert("No clock-in entries found for this week.");
      }
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to auto-populate");
    }
  };

  const addEntry = () => {
    setEntries([...entries, { 
      date: weekStart, 
      job_id: "", 
      start_time: "", 
      finish_time: "", 
      hours: 0, 
      travel_hours: 0,
      notes: "", 
      living_away_allowance: false 
    }]);
  };

  const updateEntry = (index, field, value) => {
    const newEntries = [...entries];
    newEntries[index][field] = value;
    
    // Auto-calculate hours if start and finish time are provided
    if ((field === 'start_time' || field === 'finish_time') && newEntries[index].start_time && newEntries[index].finish_time) {
      const [startHour, startMin] = newEntries[index].start_time.split(':').map(Number);
      const [endHour, endMin] = newEntries[index].finish_time.split(':').map(Number);
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;
      const diffMinutes = endMinutes - startMinutes;
      if (diffMinutes > 0) {
        newEntries[index].hours = Math.round((diffMinutes / 60) * 100) / 100;
      }
    }
    
    setEntries(newEntries);
  };

  const removeEntry = (index) => {
    setEntries(entries.filter((_, i) => i !== index));
  };

  // Reimbursement functions
  const addReimbursement = () => {
    setReimbursements([...reimbursements, { 
      expense_type: "fuel", 
      amount: 0, 
      description: "", 
      receipt_url: null, 
      no_receipt_reason: "" 
    }]);
  };

  const updateReimbursement = (index, field, value) => {
    const newReimbursements = [...reimbursements];
    newReimbursements[index][field] = value;
    setReimbursements(newReimbursements);
  };

  const removeReimbursement = (index) => {
    setReimbursements(reimbursements.filter((_, i) => i !== index));
  };

  const handleReceiptUpload = async (index, file) => {
    if (!file) return;
    try {
      toast.info("Uploading receipt...");
      const r = await uploadReceipt(file);
      updateReimbursement(index, 'receipt_url', r.url);
      toast.success("Receipt uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Receipt upload failed");
    }
  };

  const handleSubmitTimesheet = async () => {
    if (entries.length === 0) {
      alert("Please add at least one entry");
      return;
    }
    if (entries.some(e => !e.job_id || e.hours <= 0)) {
      alert("Please fill in all entries with a job and hours");
      return;
    }
    
    // Validate reimbursements
    for (let i = 0; i < reimbursements.length; i++) {
      const r = reimbursements[i];
      if (r.amount <= 0) {
        alert(`Reimbursement #${i+1}: Please enter a valid amount`);
        return;
      }
      if (!r.description) {
        alert(`Reimbursement #${i+1}: Please enter a description`);
        return;
      }
      if (!r.receipt_url && !r.no_receipt_reason) {
        alert(`Reimbursement #${i+1}: Please upload a receipt OR provide a reason for no receipt`);
        return;
      }
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/timesheets/submit`, {
        week_start: weekStart,
        entries: entries,
        reimbursements: reimbursements
      });
      alert("Timesheet submitted for approval!");
      setEntries([]);
      setReimbursements([]);
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to submit timesheet");
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (timesheetId, status) => {
    const notes = status === 'rejected' ? window.prompt("Reason for rejection:") : null;
    try {
      await axios.put(`${API}/timesheets/${timesheetId}/approve`, { status, notes });
      alert(`Timesheet ${status}`);
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to update timesheet");
    }
  };

  const handleReimbursementApproval = async (timesheetId, reimbursementId, status) => {
    const notes = status === 'rejected' ? window.prompt("Reason for rejection:") : null;
    try {
      await axios.put(`${API}/timesheets/${timesheetId}/reimbursement/${reimbursementId}/approve`, { 
        status, 
        notes,
        reimbursement_index: 0 // Not used but required by model
      });
      alert(`Reimbursement ${status}`);
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to update reimbursement");
    }
  };

  const handleAssignTimesheet = async (timesheetId, assignToId) => {
    try {
      await axios.put(`${API}/timesheets/${timesheetId}/assign`, { assigned_to: assignToId });
      alert("Timesheet assigned successfully");
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to assign timesheet");
    }
  };

  const canAssignTimesheets = ['admin', 'manager', 'office_staff'].includes(user?.role);
  const approvers = users.filter(u => ['admin', 'manager', 'supervisor'].includes(u.role));

  const handleCompare = async () => {
    if (!selectedUser || !compareWeek) {
      alert("Please select a user and week");
      return;
    }
    setLoadingCompare(true);
    try {
      const res = await axios.get(`${API}/timesheets/compare/${selectedUser}?week_start=${compareWeek}`);
      setComparison(res.data);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to load comparison");
    } finally {
      setLoadingCompare(false);
    }
  };

  const formatHours = (hours) => {
    if (!hours) return "0h";
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  const formatCurrency = (amount) => `$${(amount || 0).toFixed(2)}`;

  const getTotalHours = () => entries.reduce((sum, e) => sum + (parseFloat(e.hours) || 0), 0);
  const getTotalTravelHours = () => entries.reduce((sum, e) => sum + (parseFloat(e.travel_hours) || 0), 0);
  const getLAADays = () => entries.filter(e => e.living_away_allowance).length;
  const getTotalReimbursements = () => reimbursements.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

  // Generate week days for entry form
  const getWeekDays = () => {
    const days = [];
    const start = new Date(weekStart);
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push({
        date: day.toISOString().split('T')[0],
        label: day.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })
      });
    }
    return days;
  };

  if (loading && timesheets.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="timesheets-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight font-heading">Timesheets</h1>
          <p className="text-slate-500 mt-1">Daily hours auto-submitted weekly (Mon-Sun) for approval</p>
        </div>
        {isApprover && pendingCount > 0 && (
          <Badge variant="warning" className="text-lg px-4 py-2">
            {pendingCount} Pending Approval
          </Badge>
        )}
      </div>

      {/* Worker banner: questions from approvers waiting on their reply */}
      {myPendingQueries.length > 0 && (
        <Card className="border-2 border-blue-300 bg-blue-50" data-testid="my-pending-queries">
          <h3 className="text-sm font-bold uppercase text-blue-800 mb-2">
            {myPendingQueries.length} question{myPendingQueries.length === 1 ? "" : "s"} about your timesheet — please reply
          </h3>
          <div className="space-y-2">
            {myPendingQueries.map(q => (
              <QueryThread
                key={q.id}
                query={q}
                currentUser={user}
                onChanged={async () => {
                  try {
                    const r = await axios.get(`${API}/timesheet-queries`, { params: { mine: true, status: "pending" } });
                    setMyPendingQueries(r.data || []);
                  } catch (e) { /* silent */ }
                }}
              />
            ))}
          </div>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        <button
          onClick={() => setActiveTab("submit")}
          className={`px-4 py-2 rounded-md font-semibold text-sm uppercase whitespace-nowrap transition-colors ${
            activeTab === "submit" ? 'bg-orange-500 text-slate-900' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          My Weeks
        </button>
        <button
          onClick={() => setActiveTab("my")}
          className={`px-4 py-2 rounded-md font-semibold text-sm uppercase whitespace-nowrap transition-colors ${
            activeTab === "my" ? 'bg-orange-500 text-slate-900' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          My Timesheets
        </button>
        {isApprover && (
          <button
            onClick={() => setActiveTab("pending")}
            className={`px-4 py-2 rounded-md font-semibold text-sm uppercase whitespace-nowrap transition-colors flex items-center gap-2 ${
              activeTab === "pending" ? 'bg-orange-500 text-slate-900' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Pending Approval
            {pendingCount > 0 && <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{pendingCount}</span>}
          </button>
        )}
        {canViewAll && (
          <>
            <button
              onClick={() => setActiveTab("approved")}
              className={`px-4 py-2 rounded-md font-semibold text-sm uppercase whitespace-nowrap transition-colors ${
                activeTab === "approved" ? 'bg-orange-500 text-slate-900' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Approved
            </button>
            {canCompareHours && (
              <button
                onClick={() => setActiveTab("compare")}
                className={`px-4 py-2 rounded-md font-semibold text-sm uppercase whitespace-nowrap transition-colors ${
                  activeTab === "compare" ? 'bg-orange-500 text-slate-900' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Compare Hours
              </button>
            )}
          </>
        )}
      </div>

      {/* Export Buttons */}
      {canViewAll && (
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm"
            onClick={async () => {
              try {
                const res = await axios.get(`${API}/timesheets/export/csv${weekStart ? `?week_start=${weekStart}` : ''}`, { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a'); a.href = url; a.download = `timesheets_${weekStart || 'all'}.csv`; a.click();
              } catch (e) { toast.error("Export failed"); }
            }}
            data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
          <Button variant="outline" size="sm"
            onClick={async () => {
              try {
                const res = await axios.get(`${API}/timesheets/export/pdf${weekStart ? `?week_start=${weekStart}` : ''}`, { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                const a = document.createElement('a'); a.href = url; a.download = `timesheets_${weekStart || 'all'}.pdf`; a.click();
              } catch (e) { toast.error("Export failed"); }
            }}
            data-testid="export-pdf-btn">
            <Download className="w-4 h-4 mr-1" /> Export PDF
          </Button>
          <Button variant="outline" size="sm"
            onClick={async () => {
              try {
                const res = await axios.get(`${API}/timesheets/export/payroll${weekStart ? `?week_start=${weekStart}` : ''}`, { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a'); a.href = url; a.download = `payroll_${weekStart || 'all'}.csv`; a.click();
              } catch (e) { toast.error("Export failed"); }
            }}
            data-testid="export-payroll-btn">
            <Download className="w-4 h-4 mr-1" /> Payroll Export
          </Button>
        </div>
      )}

      {/* My Weeks Tab — shows weekly timesheets with daily entries */}
      {activeTab === "submit" && (
        <div className="space-y-4">
          <div className="flex items-center gap-4 mb-2">
            <label className="text-sm font-bold text-slate-600">Week:</label>
            <input type="date" value={weekStart} onChange={(e) => setWeekStart(e.target.value)}
              className="px-3 py-2 border-2 border-slate-200 rounded-lg text-sm" style={{ fontSize: '16px' }} />
          </div>

          {/* Show the current week's timesheet */}
          {(() => {
            const myTs = timesheets.find(t => t.user_id === user?.id && t.week_start === weekStart);
            const myEntries = myTs?.original_entries || [];
            const totalHrs = myEntries.reduce((sum, e) => sum + (e.total_hours || 0), 0);
            const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
            
            // Group entries by day
            const byDay = {};
            myEntries.forEach(e => {
              const d = new Date(e.date || e.clock_in);
              const dayName = days[d.getDay() === 0 ? 6 : d.getDay() - 1];
              if (!byDay[dayName]) byDay[dayName] = [];
              byDay[dayName].push(e);
            });

            return (
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold uppercase">Week of {weekStart}</h3>
                    <p className="text-sm text-slate-500">
                      {myTs ? (
                        <>Status: <Badge variant={myTs.status === 'approved' ? 'success' : myTs.status === 'pending' ? 'warning' : myTs.status === 'rejected' ? 'destructive' : 'default'}>
                          {myTs.status === 'pending' ? 'Submitted for Approval' : myTs.status?.toUpperCase()}
                        </Badge>
                        {myTs.auto_submitted && <span className="text-xs text-slate-400 ml-2">(auto-submitted)</span>}
                        </>
                      ) : 'No entries yet — hours auto-populate when you clock in/out'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-orange-500">{totalHrs.toFixed(1)}h</p>
                    <p className="text-xs text-slate-400">Hours This Week</p>
                  </div>
                </div>

                {/* Daily Breakdown */}
                <div className="space-y-2">
                  {days.map(day => {
                    const dayEntries = byDay[day] || [];
                    const dayTotal = dayEntries.reduce((s, e) => s + (e.total_hours || 0), 0);
                    return (
                      <div key={day} className={`flex items-center gap-3 p-3 rounded-lg ${dayEntries.length > 0 ? 'bg-green-50 border border-green-200' : 'bg-slate-50 border border-slate-100'}`}>
                        <span className={`text-sm font-bold w-24 ${dayEntries.length > 0 ? 'text-green-800' : 'text-slate-400'}`}>{day.slice(0, 3)}</span>
                        <div className="flex-1">
                          {dayEntries.length > 0 ? dayEntries.map((e, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm">
                              <span className="text-slate-600">{e.job_title || 'Job'}</span>
                              <span className="text-slate-400">|</span>
                              <span className="text-slate-500">{(e.clock_in || '').slice(11, 16)} - {(e.clock_out || '').slice(11, 16)}</span>
                              <span className="font-bold text-green-700">{(e.total_hours || 0).toFixed(1)}h</span>
                            </div>
                          )) : (
                            <span className="text-xs text-slate-400">No hours</span>
                          )}
                        </div>
                        {dayTotal > 0 && <span className="text-sm font-bold text-orange-600">{dayTotal.toFixed(1)}h</span>}
                      </div>
                    );
                  })}
                </div>

                {/* Supervisor info */}
                {myTs?.supervisor_name && (
                  <div className="mt-4 pt-3 border-t flex items-center justify-between text-sm">
                    <span className="text-slate-500">Supervisor: <strong>{myTs.supervisor_name}</strong></span>
                    {myTs.submitted_at && <span className="text-slate-400">Submitted: {new Date(myTs.submitted_at).toLocaleDateString('en-AU')}</span>}
                  </div>
                )}
              </Card>
            );
          })()}

          {/* Previous weeks */}
          <h4 className="text-sm font-bold uppercase text-slate-400 mt-6">Previous Weeks</h4>
          {timesheets.filter(t => t.user_id === user?.id && t.week_start !== weekStart).length > 0 ? (
            timesheets.filter(t => t.user_id === user?.id && t.week_start !== weekStart).map(ts => (
              <Card key={ts.id} className="cursor-pointer hover:border-orange-300" onClick={() => setWeekStart(ts.week_start)}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold">Week of {ts.week_start}</p>
                    <p className="text-xs text-slate-500">{(ts.original_entries || []).length} entries</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={ts.status === 'approved' ? 'success' : ts.status === 'pending' ? 'warning' : ts.status === 'rejected' ? 'destructive' : 'default'}>
                      {ts.status === 'pending' ? 'Awaiting Approval' : ts.status?.toUpperCase()}
                    </Badge>
                    <span className="text-lg font-bold text-orange-500">{ts.total_submitted_hours?.toFixed(1) || '0.0'}h</span>
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <p className="text-sm text-slate-400 text-center py-4">No previous timesheets</p>
          )}
        </div>
      )}

      {/* My Timesheets Tab */}
      {activeTab === "my" && (
        <div className="space-y-4">
          {timesheets.filter(t => t.user_id === user?.id).length > 0 ? (
            timesheets.filter(t => t.user_id === user?.id).map(ts => (
              <Card key={ts.id}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold">Week of {new Date(ts.week_start).toLocaleDateString()}</h3>
                    <p className="text-sm text-slate-500">
                      {ts.entries?.length || 0} entries • Work: {formatHours(ts.total_submitted_hours)}
                      {ts.total_travel_hours > 0 && <span className="text-blue-600 ml-2">• Travel: {formatHours(ts.total_travel_hours)}</span>}
                      {ts.laa_days > 0 && <span className="text-purple-600 ml-2">• {ts.laa_days} LAA days</span>}
                    </p>
                    {ts.reimbursements?.length > 0 && (
                      <p className="text-sm text-green-600">
                        {ts.reimbursements.length} reimbursement(s) • {formatCurrency(ts.total_reimbursements)}
                      </p>
                    )}
                    <p className="text-xs text-slate-400 mt-1">
                      Submitted {new Date(ts.submitted_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge variant={
                    ts.status === 'approved' ? 'success' : 
                    ts.status === 'rejected' ? 'error' : 'warning'
                  }>
                    {ts.status}
                  </Badge>
                </div>
                {ts.assigned_to_name && (
                  <p className="text-xs text-blue-600 mt-1">
                    Assigned to: {ts.assigned_to_name}
                  </p>
                )}
                {ts.approval_notes && (
                  <p className="mt-3 text-sm text-slate-600 bg-slate-50 p-2 rounded">
                    {ts.status === 'rejected' ? '❌' : '✅'} {ts.approval_notes}
                    {ts.approved_by_name && <span className="text-xs text-slate-400 ml-2">- {ts.approved_by_name}</span>}
                  </p>
                )}
                {/* Show reimbursement statuses */}
                {ts.reimbursements?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <p className="text-xs font-semibold text-slate-500 mb-2">REIMBURSEMENTS:</p>
                    {ts.reimbursements.map((r, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm py-1">
                        <span>{r.expense_type} - {r.description}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{formatCurrency(r.amount)}</span>
                          <Badge variant={
                            r.status === 'approved' ? 'success' : 
                            r.status === 'rejected' ? 'error' : 'warning'
                          } className="text-xs">
                            {r.status === 'pending_approval' ? 'Pending' : r.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            ))
          ) : (
            <div className="text-center py-12 text-slate-500">
              <ClipboardList className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-lg font-semibold">No timesheets submitted</p>
              <p className="text-sm">Go to "Submit Hours" to create your first timesheet</p>
            </div>
          )}
        </div>
      )}

      {/* Pending Approval Tab - Grouped by EMPLOYEE with expandable Mon-Sun breakdown */}
      {activeTab === "pending" && isApprover && (
        <div className="space-y-3">
          {(() => {
            const pending = timesheets.filter(t => t.status === 'pending' || t.status === 'assigned');
            if (pending.length === 0) {
              return (
                <div className="text-center py-12 text-slate-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-300" />
                  <p className="text-lg font-semibold">All caught up!</p>
                  <p className="text-sm">No timesheets pending approval</p>
                </div>
              );
            }

            // Group by EMPLOYEE first
            const byEmployee = {};
            pending.forEach(ts => {
              const key = ts.user_id || ts.user_name || 'unknown';
              if (!byEmployee[key]) {
                byEmployee[key] = { name: ts.user_name || 'Unknown', user_id: ts.user_id, timesheets: [] };
              }
              byEmployee[key].timesheets.push(ts);
            });
            const employees = Object.values(byEmployee).sort((a, b) => a.name.localeCompare(b.name));

            const DAYS = [
              { key: 'Mon', long: 'Monday',    idx: 0 },
              { key: 'Tue', long: 'Tuesday',   idx: 1 },
              { key: 'Wed', long: 'Wednesday', idx: 2 },
              { key: 'Thu', long: 'Thursday',  idx: 3 },
              { key: 'Fri', long: 'Friday',    idx: 4 },
              { key: 'Sat', long: 'Saturday',  idx: 5 },
              { key: 'Sun', long: 'Sunday',    idx: 6 },
            ];

            const addDays = (isoDate, n) => {
              const d = new Date(isoDate);
              d.setDate(d.getDate() + n);
              return d;
            };
            const fmtDate = (d) => `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}`;

            return employees.map((emp) => {
              const isOpen = !!expandedEmployees[emp.user_id || emp.name];
              const empTotal = emp.timesheets.reduce((s, t) => s + (t.total_submitted_hours || 0), 0);
              const empTravel = emp.timesheets.reduce((s, t) => s + (t.total_travel_hours || 0), 0);
              const empLAA = emp.timesheets.reduce((s, t) => s + (t.laa_days || 0), 0);
              const empReimb = emp.timesheets.reduce((s, t) => s + ((t.reimbursements || []).reduce((ss, r) => ss + (r.amount || 0), 0)), 0);

              return (
                <Card key={emp.user_id || emp.name} className="border-2 border-orange-200 overflow-hidden" data-testid={`pending-employee-${emp.user_id || emp.name}`}>
                  {/* Employee header row — click to expand */}
                  <button
                    type="button"
                    onClick={() => setExpandedEmployees(prev => ({ ...prev, [emp.user_id || emp.name]: !prev[emp.user_id || emp.name] }))}
                    className="w-full flex items-center justify-between gap-3 text-left hover:bg-orange-50/50 -m-4 p-4 transition-colors"
                    data-testid={`employee-row-${emp.user_id || emp.name}`}
                  >
                    <div className="flex items-center gap-3">
                      {isOpen ? <ChevronDown className="w-5 h-5 text-orange-500" /> : <ChevronRight className="w-5 h-5 text-orange-500" />}
                      <div>
                        <h3 className="text-base font-bold">{emp.name}</h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {emp.timesheets.length} week{emp.timesheets.length === 1 ? '' : 's'} pending · {empTotal.toFixed(1)}h work
                          {empTravel > 0 && <> · {empTravel.toFixed(1)}h travel</>}
                          {empLAA > 0 && <> · {empLAA} LAA</>}
                          {empReimb > 0 && <> · {formatCurrency(empReimb)} reimb</>}
                        </p>
                      </div>
                    </div>
                    <Badge variant="warning">{emp.timesheets.length} pending</Badge>
                  </button>

                  {/* Expanded view — each week as Mon-Sun breakdown */}
                  {isOpen && (
                    <div className="mt-4 pt-4 border-t-2 border-dashed border-slate-200 space-y-4">
                      {emp.timesheets
                        .slice()
                        .sort((a, b) => (b.week_start || '').localeCompare(a.week_start || ''))
                        .map(ts => {
                          const weekStart = ts.week_start;
                          const weekLabel = weekStart ? new Date(weekStart).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Unknown week';
                          return (
                            <div key={ts.id} className="rounded-lg border bg-slate-50 p-3" data-testid={`pending-ts-${ts.id}`}>
                              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                                <div>
                                  <h4 className="font-bold text-sm">Week of {weekLabel} (Mon–Sun)</h4>
                                  <p className="text-xs text-slate-500">
                                    {(ts.total_submitted_hours || 0).toFixed(1)}h work
                                    {ts.total_travel_hours > 0 && <> · {ts.total_travel_hours.toFixed(1)}h travel</>}
                                    {ts.laa_days > 0 && <> · {ts.laa_days} LAA day{ts.laa_days === 1 ? '' : 's'}</>}
                                    {ts.auto_submitted && <> · auto-submitted</>}
                                  </p>
                                </div>
                                <div className="flex gap-2">
                                  {canAssignTimesheets && !ts.assigned_to && (
                                    <select
                                      className="text-xs border rounded px-2 py-1"
                                      onChange={(e) => e.target.value && handleAssignTimesheet(ts.id, e.target.value)}
                                      defaultValue=""
                                    >
                                      <option value="">Assign...</option>
                                      {approvers.map(a => <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>)}
                                    </select>
                                  )}
                                  <Button size="sm" variant="secondary" onClick={() => handleApprove(ts.id, 'approved')} data-testid={`approve-${ts.id}`}>
                                    <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve week
                                  </Button>
                                  <Button size="sm" variant="destructive" onClick={() => handleApprove(ts.id, 'rejected')} data-testid={`reject-${ts.id}`}>
                                    <X className="w-3.5 h-3.5 mr-1" /> Reject
                                  </Button>
                                </div>
                              </div>

                              {/* Approval requirements summary — calls out what needs admin
                                  attention BEFORE they hit Approve */}
                              {(() => {
                                const reimbs = ts.reimbursements || [];
                                const pendingReimbs = reimbs.filter(r => (r.status || 'pending') !== 'approved' && (r.status || 'pending') !== 'rejected');
                                const reimbTotal = pendingReimbs.reduce((s, r) => s + (r.amount || 0), 0);
                                const hasTravel = (ts.total_travel_hours || 0) > 0;
                                const hasLAA = (ts.laa_days || 0) > 0;
                                const isIncomplete = ts.needs_review || ts.incomplete || ((ts.total_submitted_hours || 0) === 0);
                                const items = [];
                                if (hasTravel) items.push({ icon: '🚗', label: `Travel: ${(ts.total_travel_hours || 0).toFixed(1)}h`, tone: 'blue' });
                                if (hasLAA) items.push({ icon: '🛏️', label: `LAA: ${ts.laa_days} night${ts.laa_days === 1 ? '' : 's'} (${formatCurrency(ts.laa_days * 40)})`, tone: 'purple' });
                                if (pendingReimbs.length > 0) items.push({ icon: '🧾', label: `${pendingReimbs.length} reimbursement${pendingReimbs.length === 1 ? '' : 's'} · ${formatCurrency(reimbTotal)}`, tone: 'amber' });
                                if (isIncomplete) items.push({ icon: '⚠️', label: 'Incomplete — needs correction', tone: 'red' });
                                if (items.length === 0) return null;
                                const toneClass = {
                                  blue: 'bg-blue-100 text-blue-800 border-blue-300',
                                  purple: 'bg-purple-100 text-purple-800 border-purple-300',
                                  amber: 'bg-amber-100 text-amber-800 border-amber-300',
                                  red: 'bg-red-100 text-red-800 border-red-300',
                                };
                                return (
                                  <div className="mb-2 p-2 rounded bg-slate-100 border border-slate-200" data-testid={`approval-requirements-${ts.id}`}>
                                    <p className="text-[10px] font-bold uppercase text-slate-500 mb-1">Requires your approval</p>
                                    <div className="flex flex-wrap gap-1.5">
                                      {items.map((it, k) => (
                                        <span key={k} className={`text-[11px] font-semibold px-2 py-1 rounded border ${toneClass[it.tone]}`} data-testid={`approval-req-${it.tone}-${ts.id}`}>
                                          {it.icon} {it.label}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Mon-Sun daily breakdown */}
                              <div className="space-y-1 bg-white rounded border border-slate-200 p-2">
                                {DAYS.map((d) => {
                                  const dayDate = weekStart ? addDays(weekStart, d.idx) : null;
                                  // Build local YYYY-MM-DD string directly (avoid toISOString UTC shift)
                                  const dayIso = dayDate
                                    ? `${dayDate.getFullYear()}-${String(dayDate.getMonth() + 1).padStart(2, '0')}-${String(dayDate.getDate()).padStart(2, '0')}`
                                    : null;
                                  // Fall back to original_entries when entries[] is empty
                                  // (happens for auto-submitted timesheets — backend pushes clock-offs into original_entries only)
                                  const sourceEntries = (ts.entries && ts.entries.length > 0)
                                    ? ts.entries
                                    : (ts.original_entries || []);
                                  const entries = sourceEntries.filter(e => e.date === dayIso);
                                  // original_entries uses `total_hours`; entries uses `hours` — accept both
                                  const hrs = entries.reduce((s, e) => s + (e.hours ?? e.total_hours ?? 0), 0);
                                  const travel = entries.reduce((s, e) => s + (e.travel_hours || 0), 0);
                                  const hasLAA = entries.some(e => e.living_away_allowance);
                                  // dayJobs = array of job titles for this day (display only).
                                  // Do NOT shadow the outer `jobs` state — `AddMissingEntryButton`
                                  // and `TimeEntryEditor` need the full jobs list to populate dropdowns.
                                  const dayJobs = [...new Set(entries.map(e => e.job_title || e.job_id).filter(Boolean))];

                                  // Reimbursements for this day
                                  const dayReimb = (ts.reimbursements || []).filter(r => r.date === dayIso);

                                  const hasAnything = hrs > 0 || travel > 0 || hasLAA || dayReimb.length > 0;

                                  return (
                                    <div
                                      key={d.key}
                                      className={`flex items-center justify-between gap-3 py-1.5 px-2 rounded text-sm ${hasAnything ? 'bg-green-50/60' : 'bg-slate-50'}`}
                                      data-testid={`day-row-${ts.id}-${d.key}`}
                                    >
                                      <div className="flex items-center gap-2 min-w-[160px]">
                                        <span className={`font-bold ${hasAnything ? 'text-slate-800' : 'text-slate-400'}`}>{d.long}</span>
                                        {dayDate && <span className="text-xs text-slate-500">{fmtDate(dayDate)}</span>}
                                      </div>
                                      <div className="flex-1 min-w-0 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                                        {hrs > 0 ? (
                                          <span className="font-bold text-green-700">{hrs.toFixed(1)}h</span>
                                        ) : (
                                          <span className="italic text-slate-400">nil hours recorded</span>
                                        )}
                                        {travel > 0 && <span className="text-blue-600">+{travel.toFixed(1)}h travel</span>}
                                        {hasLAA && <span className="text-purple-600 font-semibold">LAA</span>}
                                        {dayJobs.length > 0 && <span className="text-slate-500 truncate">· {dayJobs.join(', ')}</span>}
                                        {dayReimb.map((r, i) => (
                                          <span key={i} className="text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                                            {r.expense_type || 'reimb'}: {formatCurrency(r.amount || 0)}
                                          </span>
                                        ))}
                                      </div>
                                      {hasAnything ? (
                                        <button
                                          type="button"
                                          onClick={() => setQuickView({
                                            ts,
                                            dayIso,
                                            dayLabel: `${d.long} · ${fmtDate(dayDate)}`,
                                            entries,
                                            dayReimb,
                                            travel,
                                            hrs,
                                            hasLAA,
                                            empName: emp.name
                                          })}
                                          className="text-[11px] font-bold uppercase text-orange-600 hover:text-orange-700 hover:underline px-2 py-1 rounded whitespace-nowrap"
                                          data-testid={`quickview-${ts.id}-${d.key}`}
                                        >
                                          Quick view
                                        </button>
                                      ) : canEditEntries ? (
                                        <AddMissingEntryButton
                                          userId={ts.user_id}
                                          userName={emp.name}
                                          dateIso={dayIso}
                                          jobs={jobs}
                                          onSaved={fetchData}
                                        />
                                      ) : null}
                                    </div>
                                  );
                                })}

                                {/* Reimbursements NOT tied to a specific day */}
                                {(ts.reimbursements || []).filter(r => !r.date).length > 0 && (
                                  <div className="mt-2 pt-2 border-t border-slate-200">
                                    <p className="text-[11px] font-bold uppercase text-slate-500 mb-1">Other reimbursements</p>
                                    {(ts.reimbursements || []).filter(r => !r.date).map((r, i) => (
                                      <div key={i} className="text-xs text-amber-700 flex gap-2">
                                        <span className="bg-amber-100 px-1.5 py-0.5 rounded">{r.expense_type || 'other'}</span>
                                        <span className="font-bold">{formatCurrency(r.amount || 0)}</span>
                                        {r.description && <span className="text-slate-500">— {r.description}</span>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </Card>
              );
            });
          })()}
        </div>
      )}

      {/* Timesheet Quick-View Drawer (side panel for approvers) */}
      {quickView && (
        <div className="fixed inset-0 z-50" data-testid="timesheet-quickview-drawer">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setQuickView(null)}
          />
          <div className="absolute right-0 top-0 h-full w-full sm:w-[420px] bg-white shadow-2xl overflow-y-auto">
            <div className="p-4 border-b sticky top-0 bg-white z-10 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-base">{quickView.empName}</h3>
                <p className="text-xs text-slate-500">{quickView.dayLabel}</p>
              </div>
              <button
                onClick={() => setQuickView(null)}
                className="p-1 rounded hover:bg-slate-100"
                data-testid="quickview-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4 text-sm">
              {/* Day totals */}
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 bg-green-50 rounded">
                  <p className="text-[10px] uppercase text-slate-500">Work</p>
                  <p className="font-bold text-green-700">{quickView.hrs.toFixed(1)}h</p>
                </div>
                <div className="p-2 bg-blue-50 rounded">
                  <p className="text-[10px] uppercase text-slate-500">Travel</p>
                  <p className="font-bold text-blue-700">{(quickView.travel || 0).toFixed(1)}h</p>
                </div>
                <div className="p-2 bg-purple-50 rounded">
                  <p className="text-[10px] uppercase text-slate-500">LAA</p>
                  <p className="font-bold text-purple-700">{quickView.hasLAA ? 'Yes' : '-'}</p>
                </div>
              </div>

              {/* Time entries */}
              <div>
                <p className="text-xs font-bold uppercase text-slate-500 mb-2">Shifts</p>
                {quickView.entries.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">No shifts recorded</p>
                ) : (
                  <div className="space-y-2">
                    {quickView.entries.map((e, i) => {
                      const entryId = e.time_entry_id || e.id;
                      const isEditing = editingEntryId && entryId && editingEntryId === entryId;
                      if (isEditing) {
                        return (
                          <TimeEntryEditor
                            key={i}
                            entry={e}
                            jobs={jobs}
                            onSaved={async () => {
                              setEditingEntryId(null);
                              await fetchData();
                              setQuickView(null);
                            }}
                            onCancel={() => setEditingEntryId(null)}
                          />
                        );
                      }
                      return (
                      <div key={i} className="p-2 bg-slate-50 rounded border border-slate-200">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-slate-800">{e.job_title || 'No job'}</span>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-orange-600">{(e.hours || e.total_hours || 0).toFixed(1)}h</span>
                            {canEditEntries && entryId && (
                              <>
                                <EditEntryButton
                                  onClick={() => setEditingEntryId(entryId)}
                                  data-testid={`edit-shift-${i}`}
                                />
                                <button
                                  type="button"
                                  onClick={async () => {
                                    if (!window.confirm("Delete this shift?")) return;
                                    try {
                                      await axios.delete(`${API}/time/entries/${entryId}`);
                                      toast.success("Shift deleted");
                                      await fetchData();
                                      setQuickView(null);
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || "Couldn't delete");
                                    }
                                  }}
                                  className="text-[11px] font-bold uppercase text-red-600 hover:text-red-800 hover:underline px-2 py-1"
                                  data-testid={`delete-shift-${i}`}
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                        <p className="text-[11px] text-slate-500">
                          {e.clock_in && new Date(e.clock_in).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
                          {' → '}
                          {e.clock_out && new Date(e.clock_out).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                        {e.work_description && <p className="text-xs text-slate-600 mt-1 italic">"{e.work_description}"</p>}
                        {e.supervisor_name && <p className="text-[10px] text-slate-400 mt-1">Supervisor: {e.supervisor_name}</p>}
                        {e.travel_hours > 0 && (
                          <p className="text-[11px] text-blue-600 mt-1">Travel: {e.travel_hours.toFixed(1)}h {e.travel_reason && `— ${e.travel_reason}`}</p>
                        )}
                        {/* Q&A thread for this shift — approvers can ask, worker can reply, history persists */}
                        <AskQuestionButton
                          timesheetId={quickView.ts.id}
                          targetType="shift"
                          targetId={entryId || `idx-${i}`}
                          targetLabel={`${quickView.dayLabel} · ${e.job_title || 'shift'}`}
                          currentUser={user}
                          existingThreads={queries}
                          onChanged={() => fetchQueriesForTimesheet(quickView.ts.id)}
                        />
                      </div>
                      );
                    })}
                    {canEditEntries && quickView.ts && quickView.dayIso && (
                      <div className="mt-2 pt-2 border-t border-dashed border-slate-200">
                        <AddMissingEntryButton
                          userId={quickView.ts.user_id}
                          userName={quickView.empName}
                          dateIso={quickView.dayIso}
                          jobs={jobs}
                          onSaved={async () => { await fetchData(); setQuickView(null); }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Reimbursements for this day */}
              {quickView.dayReimb.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase text-slate-500 mb-2">Reimbursements</p>
                  <div className="space-y-2">
                    {quickView.dayReimb.map((r, i) => (
                      <div key={i} className="p-2 bg-amber-50 rounded border border-amber-200">
                        <div className="flex items-center justify-between mb-1">
                          <Badge variant="warning">{r.expense_type || 'other'}</Badge>
                          <span className="font-bold text-amber-800">{formatCurrency(r.amount || 0)}</span>
                        </div>
                        {r.description && <p className="text-xs text-slate-700">{r.description}</p>}
                        {r.no_receipt_reason && !r.receipt_url && (
                          <p className="text-[11px] text-red-600 italic mt-1">No receipt: {r.no_receipt_reason}</p>
                        )}
                        {r.receipt_url && (
                          <a href={r.receipt_url} target="_blank" rel="noreferrer">
                            <img
                              src={r.receipt_url}
                              alt="receipt"
                              className="mt-2 w-full max-h-48 object-contain rounded border bg-white"
                            />
                          </a>
                        )}
                        {isReimbursementApprover && r.status !== 'approved' && (
                          <Button
                            size="sm"
                            variant="secondary"
                            className="mt-2 w-full"
                            onClick={() => handleReimbursementApproval(quickView.ts.id, r.id || i, 'approved')}
                            data-testid={`quickview-approve-reimb-${i}`}
                          >
                            Approve reimbursement
                          </Button>
                        )}
                        <AskQuestionButton
                          timesheetId={quickView.ts.id}
                          targetType="reimbursement"
                          targetId={r.id || `reimb-${i}`}
                          targetLabel={`${r.expense_type || 'reimb'} ${formatCurrency(r.amount || 0)} · ${quickView.dayLabel}`}
                          currentUser={user}
                          existingThreads={queries}
                          onChanged={() => fetchQueriesForTimesheet(quickView.ts.id)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Working Away (LAA) — approver toggle + dinner receipt for this day.
                  Worker can also see/edit own from the My Weeks tab. */}
              {quickView.dayIso && (quickView.ts.user_id === user?.id || canEditEntries) && (
                <div>
                  <p className="text-xs font-bold uppercase text-slate-500 mb-2">Living Away Allowance</p>
                  <WorkingAwayCard
                    dateIso={quickView.dayIso}
                    compact
                    onChanged={async () => { await fetchData(); }}
                  />
                </div>
              )}

              {/* General week-level queries (not tied to a specific shift/reimb) */}
              <div>
                <p className="text-xs font-bold uppercase text-slate-500 mb-2">Questions on this timesheet</p>
                {queries.length === 0 && !canEditEntries && (
                  <p className="text-[11px] text-slate-400 italic">No questions from your approver.</p>
                )}
                {queries.map(q => (
                  <QueryThread
                    key={q.id}
                    query={q}
                    currentUser={user}
                    onChanged={() => fetchQueriesForTimesheet(quickView.ts.id)}
                  />
                ))}
                {canEditEntries && (
                  <AskQuestionButton
                    timesheetId={quickView.ts.id}
                    targetType="general"
                    targetLabel={`Week of ${quickView.ts.week_start}`}
                    currentUser={user}
                    existingThreads={[]}
                    onChanged={() => fetchQueriesForTimesheet(quickView.ts.id)}
                  />
                )}
              </div>

              {/* Approve/reject shortcuts */}
              <div className="pt-3 border-t">
                <p className="text-[11px] text-slate-500 mb-2">Approve/reject the full week from here:</p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    className="flex-1"
                    onClick={() => { handleApprove(quickView.ts.id, 'approved'); setQuickView(null); }}
                    data-testid="quickview-approve-week"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" /> Approve week
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    className="flex-1"
                    onClick={() => { handleApprove(quickView.ts.id, 'rejected'); setQuickView(null); }}
                    data-testid="quickview-reject-week"
                  >
                    <X className="w-4 h-4 mr-1" /> Reject
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approved Tab */}
      {activeTab === "approved" && canViewAll && (
        <div className="space-y-4">
          {timesheets.filter(t => t.status === 'approved').length > 0 ? (
            timesheets.filter(t => t.status === 'approved').map(ts => (
              <Card key={ts.id}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold">{ts.user_name}</h3>
                    <p className="text-sm text-slate-600">
                      Week of {new Date(ts.week_start).toLocaleDateString()} • {formatHours(ts.total_submitted_hours)}
                    </p>
                    <p className="text-xs text-slate-400">
                      Approved by {ts.approved_by_name} on {new Date(ts.approved_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge variant="success">Approved</Badge>
                </div>
              </Card>
            ))
          ) : (
            <div className="text-center py-12 text-slate-500">
              <ClipboardList className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-lg font-semibold">No approved timesheets</p>
            </div>
          )}
        </div>
      )}

      {/* Compare Hours Tab */}
      {activeTab === "compare" && canViewAll && (
        <div className="space-y-6">
          <Card>
            <h3 className="text-lg font-bold uppercase mb-4">Compare Submitted vs Tracked Hours</h3>
            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <Select
                label="Employee"
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                options={[
                  { value: "", label: "Select Employee" },
                  ...users.map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name}` }))
                ]}
              />
              <Input
                label="Week Starting"
                type="date"
                value={compareWeek}
                onChange={(e) => setCompareWeek(e.target.value)}
              />
              <div className="flex items-end">
                <Button onClick={handleCompare} disabled={loadingCompare}>
                  {loadingCompare ? <Spinner size="sm" /> : "Compare"}
                </Button>
              </div>
            </div>
          </Card>

          {comparison && (
            <Card>
              <h3 className="text-lg font-bold mb-4">{comparison.user_name} - Week of {new Date(comparison.week_start).toLocaleDateString()}</h3>
              
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600">{formatHours(comparison.submitted_total_hours)}</p>
                  <p className="text-sm text-blue-700">Submitted Hours</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{formatHours(comparison.tracked_total_hours)}</p>
                  <p className="text-sm text-green-700">Tracked Hours (Clock In/Out)</p>
                </div>
                <div className={`p-4 rounded-lg text-center ${comparison.difference > 0 ? 'bg-red-50' : comparison.difference < 0 ? 'bg-amber-50' : 'bg-slate-50'}`}>
                  <p className={`text-2xl font-bold ${comparison.difference > 0 ? 'text-red-600' : comparison.difference < 0 ? 'text-amber-600' : 'text-slate-600'}`}>
                    {comparison.difference > 0 ? '+' : ''}{formatHours(comparison.difference)}
                  </p>
                  <p className={`text-sm ${comparison.difference > 0 ? 'text-red-700' : comparison.difference < 0 ? 'text-amber-700' : 'text-slate-700'}`}>
                    Difference
                  </p>
                </div>
              </div>

              {/* Status */}
              <div className="mb-4">
                <Badge variant={
                  comparison.timesheet_status === 'approved' ? 'success' :
                  comparison.timesheet_status === 'pending' ? 'warning' :
                  comparison.timesheet_status === 'rejected' ? 'error' : 'default'
                }>
                  Timesheet: {comparison.timesheet_status === 'not_submitted' ? 'Not Submitted' : comparison.timesheet_status}
                </Badge>
              </div>

              {/* Job Breakdown */}
              {comparison.job_breakdown?.length > 0 && (
                <div>
                  <h4 className="font-bold mb-3">Breakdown by Job</h4>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-slate-200">
                        <th className="text-left py-2 text-sm font-semibold text-slate-600">Job</th>
                        <th className="text-right py-2 text-sm font-semibold text-slate-600">Submitted</th>
                        <th className="text-right py-2 text-sm font-semibold text-slate-600">Tracked</th>
                        <th className="text-right py-2 text-sm font-semibold text-slate-600">Diff</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparison.job_breakdown.map((job, idx) => (
                        <tr key={idx} className="border-b border-slate-100">
                          <td className="py-2">{job.job_name}</td>
                          <td className="py-2 text-right text-blue-600">{formatHours(job.submitted_hours)}</td>
                          <td className="py-2 text-right text-green-600">{formatHours(job.tracked_hours)}</td>
                          <td className={`py-2 text-right font-medium ${job.difference > 0 ? 'text-red-600' : job.difference < 0 ? 'text-amber-600' : 'text-slate-600'}`}>
                            {job.difference > 0 ? '+' : ''}{formatHours(job.difference)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default TimesheetsPage;
